/**
 * CalculatorForm — auto-generates an accessible form from a calculator's
 * `input_schema` (returned by the backend). Drives the public calculator
 * page; the schema also drives the same form on the admin preview later.
 */
import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";

function emptyState(schema) {
    const v = {};
    for (const f of schema || []) {
        v[f.key] = f.default !== undefined && f.default !== null ? f.default : (
            f.type === "boolean" ? false : ""
        );
    }
    return v;
}

export default function CalculatorForm({
    schema, busy, onSubmit, onReset, submitLabel = "Calculate",
}) {
    const [values, setValues] = useState(() => emptyState(schema));

    useEffect(() => {
        // Re-seed when the parent switches calculators.
        setValues(emptyState(schema));
    }, [schema]);

    const updateField = (key, val) => {
        setValues((prev) => ({ ...prev, [key]: val }));
    };

    const submit = (e) => {
        e.preventDefault();
        onSubmit?.(values);
    };

    const reset = () => {
        setValues(emptyState(schema));
        onReset?.();
    };

    return (
        <form
            onSubmit={submit}
            className="space-y-5"
            data-testid="calculator-form"
        >
            <div className="space-y-4">
                {(schema || []).map((f) => (
                    <div key={f.key} className="space-y-1.5">
                        <label
                            htmlFor={`calc-input-${f.key}`}
                            className="block text-sm font-medium text-bl"
                        >
                            {f.label}
                            {f.required && (
                                <span className="text-[var(--bl-rust)] ml-1">*</span>
                            )}
                        </label>

                        {f.type === "select" && (
                            <select
                                id={`calc-input-${f.key}`}
                                value={values[f.key] ?? ""}
                                onChange={(e) => updateField(f.key, e.target.value)}
                                className="bl-input w-full"
                                required={f.required}
                                data-testid={`calc-input-${f.key}`}
                            >
                                <option value="" disabled hidden>
                                    Select an option…
                                </option>
                                {(f.options || []).map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        )}

                        {f.type === "boolean" && (
                            <div className="flex items-center gap-3">
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                    <input
                                        id={`calc-input-${f.key}`}
                                        type="checkbox"
                                        checked={!!values[f.key]}
                                        onChange={(e) => updateField(f.key, e.target.checked)}
                                        className="h-4 w-4 rounded border-bl/40 text-bl-teal focus:ring-bl-teal"
                                        data-testid={`calc-input-${f.key}`}
                                    />
                                    <span className="text-sm text-bl-muted">
                                        {values[f.key] ? "Yes" : "No"}
                                    </span>
                                </label>
                            </div>
                        )}

                        {f.type === "number" && (
                            <input
                                id={`calc-input-${f.key}`}
                                type="number"
                                value={values[f.key] ?? ""}
                                onChange={(e) => updateField(f.key, e.target.value)}
                                className="bl-input w-full"
                                required={f.required}
                                min={f.min}
                                max={f.max}
                                step={f.step}
                                data-testid={`calc-input-${f.key}`}
                            />
                        )}

                        {f.type === "text" && (
                            <input
                                id={`calc-input-${f.key}`}
                                type="text"
                                value={values[f.key] ?? ""}
                                onChange={(e) => updateField(f.key, e.target.value)}
                                className="bl-input w-full"
                                required={f.required}
                                data-testid={`calc-input-${f.key}`}
                            />
                        )}

                        {f.type === "date" && (
                            <input
                                id={`calc-input-${f.key}`}
                                type="date"
                                value={values[f.key] ?? ""}
                                onChange={(e) => updateField(f.key, e.target.value)}
                                className="bl-input w-full"
                                required={f.required}
                                data-testid={`calc-input-${f.key}`}
                            />
                        )}

                        {f.helper && (
                            <p className="text-xs text-bl-muted">{f.helper}</p>
                        )}
                    </div>
                ))}
            </div>

            <div className="flex gap-3 pt-2">
                <button
                    type="submit"
                    disabled={busy}
                    className="bl-btn-primary flex-1 sm:flex-none"
                    data-testid="calc-submit-btn"
                >
                    {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    {submitLabel}
                </button>
                <button
                    type="button"
                    onClick={reset}
                    className="bl-btn-ghost"
                    data-testid="calc-reset-btn"
                >
                    <RefreshCw className="h-4 w-4" />
                    Reset
                </button>
            </div>
        </form>
    );
}
